<template>
  <div
    id="app"
    :class="{ 'app--no-padding': isPracticePage }"
    :style="isPracticePage ? appNoPaddingStyle : undefined"
  >
    <!--
      ブラウザ警告バナー（問い合わせ対応用）
      - 基本: デフォルトで「非推奨」扱い（ホワイトリスト外は警告）
      - 通過: Google Chrome 本体・Apple Safari 本体のみ（UA で判定）
      - LINE内 / フィルター系は従来どおり専用文言
    -->
    <div v-if="showBrowserWarning" class="alert-banner">
      <!-- 問い合わせ「LINEの中で開いている」→ この段 -->
      <p v-if="isLineBrowser">
        ⚠️ LINEの中で開いています。右上の「︙」から「ブラウザで開く」を選んでください。
      </p>
      <!-- 問い合わせ「あんしんフィルター/i-フィルター」→ この段 -->
      <p v-else-if="isFilteringBrowser">
        ⚠️ あんしんフィルター等の制限用ブラウザです。マイクが使えないため、Chrome/Safariで開き直してください。
      </p>
      <!-- 問い合わせ「その他のブラウザ / マイクが効かない」→ この段 -->
      <p v-else>
        ⚠️ 非推奨のブラウザです。マイクが動作しない場合は Chrome または Safari をご利用ください。
      </p>
    </div>
    <div class="app-content">
      <!-- ルーター初回ナビゲーション完了前はローディング表示 -->
      <div v-if="appShell === 'loading'" class="app-loading">
        <div class="app-loading-spinner"></div>
      </div>

      <!-- 講師ページの場合は独立したレイアウト -->
      <router-view v-else-if="appShell === 'instructor'" />

      <!-- /signup/verify はレイアウトなしで表示（Magic Link 後の Stripe 誘導） -->
      <router-view v-else-if="appShell === 'signup_verify'" />

      <!--
        ログイン入口（/・/manager/login）と回復画面は、認証状態にかかわらずレイアウトなしで表示する。
        認証成功（SIGNED_IN）の瞬間に外枠が変わってログイン画面が破棄・再生成されるのを防ぐ（指示書 A1）。
      -->
      <router-view v-else-if="appShell === 'bare'" />

      <!-- 認証済みの生徒の場合はメインアプリケーションレイアウト -->
      <AppLayout v-else-if="appShell === 'app'" />

      <!-- 未認証の場合はログイン画面 -->
      <router-view v-else />
    </div>
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from './supabaseClient'
import AppLayout from './components/layout/AppLayout.vue'
import AppFooter from './components/layout/AppFooter.vue'
// 1. Sentry をインポート
import * as Sentry from "@sentry/vue";
import { useAuthProfile } from './composables/useAuthProfile'
import { chooseAppShell } from './utils/appShell.js'

const route = useRoute()
const router = useRouter()
const isAuthenticated = ref(false)
const routerReady = ref(false)
router.isReady().then(() => { routerReady.value = true })

// --- ブラウザ判定（User-Agent）※問い合わせ時はバナー文言の段と突き合わせ ---
// 方針: デフォルトは警告。Google Chrome（本体）と Apple Safari（本体）だけホワイトリスト通過。
// （Samsung / Edge / Firefox 等は UA に chrome が付いていても製品名で除外）
const ua = typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : ''

/**
 * Chromium 系の「Chrome 以外」・主要代替ブラウザ・SNSアプリ内など（ホワイトリストに含めない）
 */
function hasNonWhitelistBrowserToken (uaStr) {
  return (
    uaStr.includes('samsungbrowser') ||
    uaStr.includes('edg/') ||
    uaStr.includes('edga') ||
    uaStr.includes('edgios') ||
    uaStr.includes('opr/') ||
    uaStr.includes('opt/') ||
    uaStr.includes('firefox/') ||
    uaStr.includes('fxios') ||
    uaStr.includes('brave/') ||
    uaStr.includes('vivaldi/') ||
    uaStr.includes('duckduckgo') ||
    uaStr.includes('ucbrowser') ||
    uaStr.includes('silk/') ||
    uaStr.includes('miuibrowser') ||
    uaStr.includes('micromessenger') ||
    uaStr.includes('qq/') ||
    uaStr.includes('fbav') ||
    uaStr.includes('instagram')
  )
}

/**
 * Google Chrome（公式）または Apple Safari（公式）とみなすか。
 * UA は偽装可能なので「目安」であることに留意。
 */
function isGoogleChromeOrAppleSafari (uaStr) {
  if (!uaStr) return false

  if (uaStr.includes('line/')) return false

  const isIOS = /iphone|ipad|ipod/.test(uaStr)
  const isAndroid = /android/.test(uaStr)
  const isAndroidWebView = uaStr.includes('; wv') || uaStr.includes(' wv)')
  const otherTok = hasNonWhitelistBrowserToken(uaStr)

  // iOS: Google Chrome = CriOS のみ
  if (uaStr.includes('crios')) {
    return !otherTok
  }

  // iOS: Apple Safari（他ブラウザは Safari 文字列を併用することがあるため除外トークンで抑止）
  if (isIOS) {
    const looksLikeSafari =
      uaStr.includes('safari') &&
      !uaStr.includes('crios') &&
      !uaStr.includes('fxios') &&
      !uaStr.includes('edgios')
    if (!looksLikeSafari) return false
    return !otherTok
  }

  // Android: Google Chrome のみ（WebView は不可）
  if (isAndroid) {
    if (isAndroidWebView) return false
    if (!uaStr.includes('chrome/')) return false
    if (otherTok) return false
    return true
  }

  // デスクトップ等: Apple Safari = Safari あり・Chrome 製品トークンなし
  const isDesktopSafari =
    uaStr.includes('safari') &&
    !uaStr.includes('chrome/') &&
    !uaStr.includes('chromium') &&
    !uaStr.includes('crios')
  if (isDesktopSafari) {
    return !otherTok
  }

  // デスクトップ: Google Chrome（Edge は Edg 等で otherTok に引っかかる）
  if (uaStr.includes('chrome/')) {
    if (otherTok) return false
    return true
  }

  return false
}

const isRecommendedBrowser = ref(isGoogleChromeOrAppleSafari(ua))

// 1. LINE内ブラウザ（専用文言）
const isLineBrowser = ref(ua.includes('line/'))

// 2. あんしんフィルター / i-フィルター 等（専用文言）
const isFilteringBrowser = ref(
  ua.includes('anshinfilter') ||
  ua.includes('anshin-filter') ||
  ua.includes('i-filter') ||
  ua.includes('digitalarts')
)

const showBrowserWarning = ref(
  isLineBrowser.value || isFilteringBrowser.value || !isRecommendedBrowser.value
)

// 日付（JST）が変わったときだけ Practice → Top にリダイレクトする用
const lastForegroundDateJST = ref('')

function getTodayJST () {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

function handleVisibilityChange () {
  if (document.hidden) {
    lastForegroundDateJST.value = getTodayJST()
    return
  }
  // フォアグラウンド復帰
  const today = getTodayJST()
  if (route.path === '/practice' && today !== lastForegroundDateJST.value) {
    router.push('/top')
  }
  lastForegroundDateJST.value = today
}

// 講師ページかどうかを判定
const isInstructorPage = computed(() => {
  return route.path.startsWith('/instructor')
})

// 外枠（レイアウト）の選択。ログイン入口・回復画面は、認証状態で外枠を変えない（指示書 A1）
const appShell = computed(() => chooseAppShell({
  routerReady: routerReady.value,
  path: route.path,
  routeName: route.name,
  isAuthenticated: isAuthenticated.value
}))

// /practice では #app の padding を外して動画を端まで表示（/practice や /practice/ どちらも）
const isPracticePage = computed(() => route.path === '/practice' || route.path.startsWith('/practice/'))

// インラインで確実に 0 にする（CSS の !important より優先させるため）
const appNoPaddingStyle = { padding: 0, margin: 0, maxWidth: 'none' }

onMounted(async () => {
  // 1. 初回読み込み時のユーザーチェック（useAuthProfile 経由で共通化）
  const { currentUser, ensureAuthProfileLoaded } = useAuthProfile()
  await ensureAuthProfileLoaded()
  const user = currentUser.value
  isAuthenticated.value = !!user

  // ★追加：ページを開いた瞬間にユーザーがいればSentryに教える
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  }

  // 2. 認証状態の変更を監視
  supabase.auth.onAuthStateChange((event, session) => {
    isAuthenticated.value = !!session?.user

    // ★追加：ログイン・ログアウトに合わせてSentryの情報を更新
    if (session?.user) {
      Sentry.setUser({ 
        id: session.user.id, 
        email: session.user.email 
      });
    } else {
      Sentry.setUser(null);
    }
  })

  // 日付（JST）が変わったときだけ Practice → Top にリダイレクト
  lastForegroundDateJST.value = getTodayJST()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style>
#app {
  display: flex;
  flex-direction: column;
  max-width: 1280px !important;
  margin: 0 auto !important;
  padding: 0.5rem;
  text-align: center !important;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
}

.app-content {
  flex: 1;
}

/* /practice 時は必ず padding ゼロ（data-v-app の階層も含めて確実に上書き） */
div#app.app--no-padding,
[data-v-app].app--no-padding {
  padding: 0 !important;
  margin: 0 !important;
  max-width: none !important;
}

* {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  padding: 0;
  background: #f8fafc;
}

.app-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.app-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.alert-banner {
  background: #fef3c7;
  border-bottom: 1px solid #f59e0b;
  padding: 0.5rem 0.75rem;
  text-align: center;
}

.alert-banner p {
  margin: 0;
  font-size: 0.85rem;
  color: #92400e;
  line-height: 1.4;
}
</style>
